import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  // Bearer token in Authorization header handles auth; no cookies needed cross-origin.
  // Setting withCredentials=true with Access-Control-Allow-Origin:* makes browsers block the response.
  withCredentials: false,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Super-admin workspace impersonation. Only sent if a workspace has been picked.
  const impersonate = localStorage.getItem("impersonate_coach_id");
  if (impersonate) config.headers["X-Impersonate-Coach-Id"] = impersonate;
  return config;
});

export const fileUrl = (storagePath) => `${API}/files/${storagePath}`;

// Photo URL helper: external (pexels/etc) URLs returned as-is, internal storage paths get fileUrl applied
export const photoUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return fileUrl(url);
};

/**
 * Compress an image File client-side before upload.
 * Resizes to max 1600px on the longest edge and re-encodes as JPEG 0.85.
 * A typical 4MB phone photo shrinks to ~300-500KB → uploads ~10x faster.
 */
export const compressImage = (file, { maxEdge = 1600, quality = 0.85 } = {}) =>
  new Promise((resolve, reject) => {
    if (!file || !file.type?.startsWith("image/")) return resolve(file);
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        canvas.toBlob((blob) => {
          URL.revokeObjectURL(url);
          if (!blob) return resolve(file);
          const newName = (file.name || "image").replace(/\.[^.]+$/, "") + ".jpg";
          resolve(new File([blob], newName, { type: "image/jpeg" }));
        }, "image/jpeg", quality);
      } catch (e) {
        URL.revokeObjectURL(url);
        resolve(file);
      }
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
